import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ConsultantSiteEventRequest,
  ConsultantSiteEventType,
  ConsultantSiteResponse,
  ConsultantSiteSlugAvailability,
  PublicConsultantSite,
  PublicConsultantSiteScoped,
  UpdateConsultantSiteRequest,
} from '@alamo/types';

import { apiClient, publicApiClient } from '../client';
import { queryKeys } from '../query-keys';

function unwrapResponse<T>(response: { data: unknown }): T {
  const body = response.data as { data?: T } | T;
  return (typeof body === 'object' && body !== null && 'data' in body ? body.data : body) as T;
}

export function useConsultantSite() {
  return useQuery({
    queryKey: queryKeys.consultantSite.me(),
    queryFn: async (): Promise<ConsultantSiteResponse> => {
      const response = await apiClient.get('/consultant-site/me');
      return unwrapResponse<ConsultantSiteResponse>(response);
    },
    staleTime: 60_000,
  });
}

export function useUpdateConsultantSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateConsultantSiteRequest): Promise<ConsultantSiteResponse> => {
      const response = await apiClient.put('/consultant-site/me', input);
      return unwrapResponse<ConsultantSiteResponse>(response);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.consultantSite.me(), data);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.consultantSite.slugAvailability(data.site.slug ?? ''),
      });
      void queryClient.invalidateQueries({ queryKey: publicConsultantSiteKeys.all });
    },
  });
}

export function useConsultantSiteSlugAvailability(slug: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.consultantSite.slugAvailability(slug),
    queryFn: async (): Promise<ConsultantSiteSlugAvailability> => {
      const response = await apiClient.get('/consultant-site/slug-availability', {
        params: { slug },
      });
      return unwrapResponse<ConsultantSiteSlugAvailability>(response);
    },
    enabled: enabled && slug.trim().length > 0,
    staleTime: 30_000,
    retry: false,
  });
}

export const publicConsultantSiteKeys = {
  all: ['public-consultant-site'] as const,
  detail: (slug: string) => ['public-consultant-site', slug] as const,
};

export type { PublicConsultantSiteScoped };

export function usePublicConsultantSite(slug: string | undefined) {
  return useQuery({
    queryKey: publicConsultantSiteKeys.detail(slug ?? ''),
    queryFn: async (): Promise<PublicConsultantSiteScoped> => {
      const response = await publicApiClient.get(
        `/public/consultant-site/${encodeURIComponent(slug ?? '')}`,
      );
      const site = unwrapResponse<PublicConsultantSite & { institutional?: boolean }>(response);
      return { ...site, institutional: site.institutional === true };
    },
    enabled: Boolean(slug),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useTrackConsultantSiteEvent(
  slug: string | undefined,
  eventType: ConsultantSiteEventType,
) {
  return useMutation({
    mutationFn: async (input: ConsultantSiteEventRequest): Promise<void> => {
      if (!slug) return;
      await publicApiClient.post(
        `/public/consultant-site/${encodeURIComponent(slug)}/events/${eventType}`,
        input,
      );
    },
  });
}
